import { createElement } from "react"
import { FormField, useField } from "@stackup/form"
import { Input } from "@stackup/inputs"
import { MonthInput, MONTHS } from "./MonthInput"

export type CompoundDatePart = "month" | "day" | "year"
export type CompoundDateValue = Record<CompoundDatePart, string>

interface Props {
  legend: string
  field: FormField<CompoundDateValue>
  parts: CompoundDatePart[]
}

const PARTS: CompoundDatePart[] = ["month", "day", "year"]
const COMPONENTS = { month: MonthInput, day: Input, year: Input }

const capitalize = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Attempt to convert the state of this component to a Date object.
 */
export const parseDate = (value: CompoundDateValue): Date | undefined => {
  const month = MONTHS.indexOf(value.month)
  if (month == -1) return

  const day = parseInt(value.day)
  if (isNaN(day)) return

  const year = parseInt(value.year)
  if (isNaN(year)) return

  return new Date(year, month, day)
}

export const CompoundDateField = (props: Props) => {
  const { field, legend, parts = PARTS } = props
  const { id, error, touched } = field

  const errorId = `${id}--error`
  const describedBy = error ? errorId : undefined

  const fields = {
    month: useField(field, "month"),
    day: useField(field, "day"),
    year: useField(field, "year"),
  }

  return (
    <fieldset className="compound-date-field" aria-describedby={describedBy}>
      <legend>{legend}</legend>

      <div className="compound-date-field__inputs">
        {parts.map((part) =>
          createElement(COMPONENTS[part], {
            key: part,
            field: fields[part],
            variant: "condensed",
            label: capitalize(part),
            className: `compound-date-field__${part}`,
          })
        )}
      </div>

      {touched && error && (
        <span role="alert" id={errorId} className="message message--problem">
          {error}
        </span>
      )}
    </fieldset>
  )
}
